/**
 * Content cache utility for hash-based deduplication
 * Uses IndexedDB for persistent storage and memory cache for fast access
 * Pure cache layer - no loading logic
 */

import { CONTENT_CACHE_CONFIG, getMaxCacheAgeMs, getMaxCacheSizeBytes } from "@/config/contentCacheConfig";

// Memory cache for blob URLs by hash
// Key: hash (with size), Value: blobUrl
const hashBlobUrlCache = new Map();

let dbPromise = null;

/**
 * Compute SHA-256 hash from ArrayBuffer
 * @param {ArrayBuffer} arrayBuffer - Data to hash
 * @returns {Promise<string>} Hexadecimal hash string
 */
async function computeSHA256(arrayBuffer) {
  const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Create hash with size (concatenation: hash_size)
 * @param {string} hash - SHA-256 hash
 * @param {number} size - Size in bytes
 * @returns {string} Hash with size: "hash_size"
 */
export function createHashWithSize(hash, size) {
  return `${hash}_${size}`;
}

/**
 * Initialize IndexedDB with content store (by hash)
 * @param {string} dbName - Database name
 * @param {number} dbVersion - Database version
 * @param {string} storeName - Content store name
 * @param {string} indexName - Index name for timestamp
 * @returns {Promise<IDBDatabase>}
 */
function initDB(dbName, dbVersion, storeName, indexName) {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, dbVersion);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create content store (hash -> blob data)
      if (!db.objectStoreNames.contains(storeName)) {
        const contentStore = db.createObjectStore(storeName, {
          keyPath: "hash",
        });
        contentStore.createIndex(indexName, indexName, { unique: false });
      }

      // Remove old mappings store if exists (migration)
      if (db.objectStoreNames.contains("mappings")) {
        db.deleteObjectStore("mappings");
      }
    };
  });

  return dbPromise;
}

/**
 * Get cached content by hash
 * @param {string} hashWithSize - Hash with size (hash_size)
 * @param {Object} config - Configuration object (optional, uses defaults if not provided)
 * @param {string} config.dbName - Database name
 * @param {string} config.storeName - Content store name
 * @param {string} config.defaultContentType - Default content type
 * @param {number} config.maxCacheAgeMs - Maximum cache age in milliseconds
 * @returns {Promise<string|null>} Blob URL or null if not cached
 */
export async function getCachedContent(hashWithSize, config = null) {
  if (!hashWithSize) return null;

  const dbName = config?.dbName || CONTENT_CACHE_CONFIG.database.name;
  const storeName = config?.storeName || CONTENT_CACHE_CONFIG.database.imagesStoreName;
  const defaultContentType = config?.defaultContentType || CONTENT_CACHE_CONFIG.defaultContentType;
  const maxCacheAgeMs = config?.maxCacheAgeMs || getMaxCacheAgeMs();

  // Check memory cache first
  const cachedBlobUrl = hashBlobUrlCache.get(hashWithSize);
  if (cachedBlobUrl) {
    return cachedBlobUrl;
  }

  // Check IndexedDB
  try {
    const db = await initDB(
      dbName,
      CONTENT_CACHE_CONFIG.database.version,
      storeName,
      CONTENT_CACHE_CONFIG.indexes.timestamp,
    );
    const transaction = db.transaction([storeName], "readonly");
    const store = transaction.objectStore(storeName);
    const request = store.get(hashWithSize);

    return new Promise((resolve, reject) => {
      request.onsuccess = async () => {
        const entry = request.result;
        if (!entry) {
          resolve(null);
          return;
        }

        // Check if cache entry is expired
        const age = Date.now() - entry.timestamp;
        if (age > maxCacheAgeMs) {
          // Remove expired entry
          await deleteContent(hashWithSize, config);
          resolve(null);
          return;
        }

        // Create blob URL from cached blob
        const blob = new Blob([entry.data], {
          type: entry.contentType || defaultContentType,
        });
        const blobUrl = URL.createObjectURL(blob);

        // Store in memory cache for faster access
        hashBlobUrlCache.set(hashWithSize, blobUrl);

        resolve(blobUrl);
      };

      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error reading content from IndexedDB by hash:", error);
    return null;
  }
}

/**
 * Save content to cache
 * @param {string|null} hash - SHA-256 hash (optional, will be computed if not provided)
 * @param {ArrayBuffer} arrayBuffer - Content data
 * @param {string} contentType - Content type
 * @param {number} size - Size in bytes (will be determined from arrayBuffer if not provided)
 * @param {Object} config - Configuration object (optional, uses defaults if not provided)
 * @param {string} config.dbName - Database name
 * @param {string} config.storeName - Content store name
 * @param {string} config.indexName - Index name for timestamp
 * @returns {Promise<string>} Blob URL
 */
export async function saveContent(hash, arrayBuffer, contentType, size, config = null) {
  const dbName = config?.dbName || CONTENT_CACHE_CONFIG.database.name;
  const storeName = config?.storeName || CONTENT_CACHE_CONFIG.database.imagesStoreName;
  const indexName = config?.indexName || CONTENT_CACHE_CONFIG.indexes.timestamp;

  // Determine actual size
  const actualSize = size || arrayBuffer.byteLength;

  // Compute hash if not provided
  let computedHash = hash && typeof hash === "string" ? hash.trim() : null;
  if (!computedHash || computedHash === "") {
    computedHash = await computeSHA256(arrayBuffer);
  }

  // Create hash with size
  const hashWithSize = createHashWithSize(computedHash, actualSize);

  // Check if content already exists in cache
  const existingBlobUrl = await getCachedContent(hashWithSize, {
    dbName,
    storeName,
    defaultContentType: contentType || CONTENT_CACHE_CONFIG.defaultContentType,
    maxCacheAgeMs: getMaxCacheAgeMs(),
  });

  if (existingBlobUrl) {
    return existingBlobUrl;
  }

  const db = await initDB(dbName, CONTENT_CACHE_CONFIG.database.version, storeName, indexName);

  const transaction = db.transaction([storeName], "readwrite");
  const store = transaction.objectStore(storeName);

  // Create object with hash and size
  const contentData = {
    hash: hashWithSize,
    data: arrayBuffer,
    contentType: contentType || CONTENT_CACHE_CONFIG.defaultContentType,
    size: actualSize,
    timestamp: Date.now(),
  };

  await new Promise((resolve, reject) => {
    const request = store.put(contentData);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });

  // Create blob URL
  const blob = new Blob([arrayBuffer], { type: contentType });
  const blobUrl = URL.createObjectURL(blob);

  // Store in memory cache
  hashBlobUrlCache.set(hashWithSize, blobUrl);

  // Cleanup old cache entries if needed
  cleanupOldCacheEntries(dbName, storeName, indexName, getMaxCacheAgeMs(), getMaxCacheSizeBytes());

  return blobUrl;
}

/**
 * Delete content from cache
 * @param {string} hashWithSize - Hash with size (hash_size)
 * @param {Object} config - Configuration object (optional, uses defaults if not provided)
 * @param {string} config.dbName - Database name
 * @param {string} config.storeName - Content store name
 * @param {string} config.indexName - Index name for timestamp
 */
export async function deleteContent(hashWithSize, config = null) {
  // Remove from memory cache and revoke blob URL
  const blobUrl = hashBlobUrlCache.get(hashWithSize);
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl);
  }
  hashBlobUrlCache.delete(hashWithSize);

  const dbName = config?.dbName || CONTENT_CACHE_CONFIG.database.name;
  const storeName = config?.storeName || CONTENT_CACHE_CONFIG.database.imagesStoreName;
  const indexName = config?.indexName || CONTENT_CACHE_CONFIG.indexes.timestamp;

  // Remove from IndexedDB
  try {
    const db = await initDB(dbName, CONTENT_CACHE_CONFIG.database.version, storeName, indexName);
    const transaction = db.transaction([storeName], "readwrite");
    const store = transaction.objectStore(storeName);
    await new Promise((resolve, reject) => {
      const request = store.delete(hashWithSize);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error deleting content from IndexedDB by hash:", error);
  }
}

/**
 * Get total cache size and clean up old entries if needed
 * @param {string} dbName - Database name
 * @param {string} storeName - Content store name
 * @param {string} indexName - Index name for timestamp
 * @param {number} maxCacheAgeMs - Maximum cache age in milliseconds
 * @param {number} maxCacheSizeBytes - Maximum cache size in bytes
 */
async function cleanupOldCacheEntries(dbName, storeName, indexName, maxCacheAgeMs, maxCacheSizeBytes) {
  try {
    const db = await initDB(dbName, CONTENT_CACHE_CONFIG.database.version, storeName, indexName);
    const transaction = db.transaction([storeName], "readonly");
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);

    const request = index.openCursor(null, "next");
    const entries = [];

    await new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          entries.push({
            hash: cursor.value.hash,
            timestamp: cursor.value.timestamp,
            size: cursor.value.size || cursor.value.data.byteLength,
          });
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });

    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a.timestamp - b.timestamp);

    // Calculate total size
    let totalSize = entries.reduce((sum, entry) => sum + entry.size, 0);

    // Remove expired entries
    const now = Date.now();
    for (const entry of entries) {
      if (now - entry.timestamp > maxCacheAgeMs) {
        await deleteContent(entry.hash, { dbName, storeName, indexName });
        totalSize -= entry.size;
      }
    }

    // Remove oldest entries if cache is too large
    for (const entry of entries) {
      if (totalSize <= maxCacheSizeBytes) break;
      if (now - entry.timestamp <= maxCacheAgeMs) {
        await deleteContent(entry.hash, { dbName, storeName, indexName });
        totalSize -= entry.size;
      }
    }
  } catch (error) {
    console.error("Error cleaning up cache:", error);
  }
}

/**
 * Clear all cached content
 * @param {Object} config - Configuration object (optional, uses defaults if not provided)
 * @param {string} config.dbName - Database name
 * @param {string} config.storeName - Content store name
 * @param {string} config.indexName - Index name for timestamp
 */
export async function clearCache(config = null) {
  const dbName = config?.dbName || CONTENT_CACHE_CONFIG.database.name;
  const storeName = config?.storeName || CONTENT_CACHE_CONFIG.database.imagesStoreName;
  const indexName = config?.indexName || CONTENT_CACHE_CONFIG.indexes.timestamp;

  try {
    const db = await initDB(dbName, CONTENT_CACHE_CONFIG.database.version, storeName, indexName);

    // Clear content store
    const transaction = db.transaction([storeName], "readwrite");
    const store = transaction.objectStore(storeName);
    await new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Clear memory cache and revoke blob URLs
    for (const blobUrl of hashBlobUrlCache.values()) {
      URL.revokeObjectURL(blobUrl);
    }
    hashBlobUrlCache.clear();
  } catch (error) {
    console.error("Error clearing cache:", error);
  }
}
